import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { FabricPickForm } from "@/components/FabricPickForm";
import { ExternalLink, ShoppingBag } from "lucide-react";
import shareImage from "@/assets/original/stock-fabric.jpg";
import heroImg from "@/assets/original/fabrication.jpg";
import { collections } from "@/lib/sunbrella";
import fabricRollsAsset from "@/assets/sunbrella-fabric-rolls-neutrals.jpg.asset.json";
import manhattanAsset from "@/assets/sunbrella-manhattan-classic-stripe.jpg.asset.json";
import pacificBlueAsset from "@/assets/sunbrella-pacific-blue-fancy-stripe.jpg.asset.json";
import beaufortBWAsset from "@/assets/sunbrella-beaufort-black-white-stripe.jpg.asset.json";
import captainNavyAsset from "@/assets/sunbrella-beaufort-captain-navy-stripe.jpg.asset.json";
import sunbrellaLogoAsset from "@/assets/sunbrella-logo.png.asset.json";

export const Route = createFileRoute("/fabrics")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Sunbrella® Fabric Showroom | Texas Made Shade — Seguin, TX",
      description: "Browse Sunbrella® fabric collections on the official Sunbrella site, then request a free quote from Texas Made Shade & Covers in Seguin, TX.",
      image: shareImage,
      imageAlt: "Sunbrella® fabric swatches at Texas Made Shade & Covers",
      socialTitle: "Sunbrella® Fabric Showroom — Texas Made Shade & Covers",
      socialDescription:
        "Browse Sunbrella® fabrics on the official Sunbrella site, then we build it for you.",
      keywords:
        "Sunbrella fabric Texas, Sunbrella swatches Seguin, Sunbrella awning fabric, Sunbrella upholstery, Sunbrella shade fabric, custom canvas Seguin, fabric showroom San Antonio",
    }),
    scripts: pageHeadScripts({
      path: "/fabrics",
      name: "Sunbrella® Fabric Showroom — Texas Made Shade",
      description: "Browse Sunbrella® fabric collections, then request a free quote from Texas Made Shade & Covers in Seguin, TX.",
    }),
  }),
  component: FabricsPage,
});

function FabricsPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        {/* HERO */}
        <section className="relative bg-navy-deep py-20 text-white lg:py-28">
          <div className="absolute inset-0">
            <img src={heroImg} alt="Sunbrella, Phifertex and Serge Ferrari outdoor fabric showroom at Texas Made Shade in Seguin, TX" className="h-full w-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/85 to-navy-deep/60" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
            <div className="font-display text-sm tracking-[0.3em] text-red">★ FABRIC SHOWROOM</div>
            <h1 className="mt-3 font-display text-5xl font-bold leading-[0.95] md:text-7xl">
              Sunbrella<sup className="text-2xl">®</sup> Library.<br />
              <span className="text-white/90">Picked, Sewn &amp; Installed in Seguin.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/80">
              Browse the official Sunbrella® collection, find the colors you love, then send us the names — we'll quote, sew and install it for you in Seguin.
            </p>
            <div className="mt-8">
              <a
                href="https://www.sunbrella.com/browse-fabrics/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-red px-7 py-3.5 font-display text-sm uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
              >
                <ExternalLink className="h-4 w-4" /> Browse Sunbrella® Fabrics
              </a>
            </div>
            <p className="mt-3 text-xs text-white/60">
              Opens sunbrella.com in a new tab. Come back here when you've picked your favorites.
            </p>
          </div>
        </section>

        {/* COLLECTIONS */}
        <section className="bg-cream py-14 lg:py-16">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="mb-2 font-display text-xs uppercase tracking-[0.3em] text-red">★ The Collections</div>
            <h2 className="font-display text-3xl font-bold uppercase text-navy-deep md:text-4xl">
              Three lines we use every day.
            </h2>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {collections.map((c) => (
                <div
                  key={c.id}
                  className="group flex flex-col border-2 border-navy-deep/10 bg-white p-6 text-left shadow-card"
                >
                  <div className="font-display text-[10px] uppercase tracking-[0.25em] text-red">{c.tagline}</div>
                  <h3 className="mt-2 font-display text-xl font-bold uppercase text-navy-deep">{c.name}</h3>
                  <p className="mt-3 flex-1 text-sm text-navy-deep/70">{c.description}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-wider text-navy-deep/60">
                    {c.bestFor.map((b) => (
                      <span key={b} className="border border-navy-deep/20 px-2 py-0.5">{b}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* STRIPES — text wraps around swatches */}
        <section className="bg-white py-14 lg:py-20">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <div className="grid gap-6 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
              <div className="min-w-0">
                <div className="mb-2 font-display text-xs uppercase tracking-[0.3em] text-red">★ Classic Awning Stripes</div>
                <h2 className="font-display text-3xl font-bold uppercase text-navy-deep md:text-4xl">
                  The stripes our customers ask for by name.
                </h2>
              </div>
              <a
                href="https://www.sunbrella.com"
                target="_blank"
                rel="noopener noreferrer external"
                className="shrink-0 justify-self-start transition hover:opacity-90 sm:justify-self-end"
                aria-label="Visit Sunbrella.com — official Sunbrella® fabric site (opens in a new tab)"
                title="Sunbrella.com — opens in a new tab"
              >
                <img
                  src={sunbrellaLogoAsset.url}
                  alt="Sunbrella® logo — registered trademark of Glen Raven, Inc.; Texas Made Shade is an authorized Sunbrella® fabricator"
                  width={220}
                  height={220}
                  loading="lazy"
                  decoding="async"
                  className="h-16 w-16 object-contain sm:h-20 sm:w-20"
                />
              </a>
            </div>



            <div className="mt-8 text-navy-deep/80 [&>p]:mt-4 [&>p]:leading-relaxed">
              <SwatchFigure
                side="right"
                src={manhattanAsset.url}
                alt="Sunbrella® Manhattan Classic 4789 awning fabric with wide black and tan stripes"
                caption="Manhattan Classic 4789"
              />

              <p>
                <strong className="text-navy-deep">Manhattan Classic</strong> is the workhorse of Texas storefronts and
                front-porch awnings — wide bands of espresso black and warm tan that hide dust and pollen between washes.
                We sew it flat-seamed on residential and restaurant awnings all over Seguin, New Braunfels and San Antonio,
                and because it's solution-dyed acrylic the color runs all the way through the fiber instead of sitting on
                top of it. Ten years in full South Texas sun and it still reads as black and tan, not gray and beige.
              </p>

              <SwatchFigure
                side="left"
                src={pacificBlueAsset.url}
                alt="Sunbrella® Pacific Blue Fancy 4755 awning fabric with multi-width royal blue and white stripes"
                caption="Pacific Blue Fancy 4755"
              />

              <p>
                Want color? <strong className="text-navy-deep">Pacific Blue Fancy</strong> layers royal, sky and navy
                against crisp white in a fancy multi-bar repeat. It's the pattern that makes a taco shop, snow-cone stand
                or lake-house patio cover look intentional instead of temporary. It pairs beautifully with white powder-coated
                frames and our in-house bent bows, and it's a favorite for drop curtains and valances where the fabric is the
                first thing a customer sees from the street.
              </p>

              <SwatchFigure
                side="right"
                src={beaufortBWAsset.url}
                alt="Sunbrella® Beaufort Black and White 6-Bar 5704 awning fabric with bold black and white stripes"
                caption="Beaufort Black & White 6-Bar 5704"
              />

              <p>
                <strong className="text-navy-deep">Beaufort Black &amp; White 6-Bar</strong> is the high-contrast, six-bar
                bistro look — clean, graphic and impossible to miss on a downtown façade. Restaurants love it for the
                European café feel, HOAs approve it because it reads as neutral, and it photographs incredibly well for
                signage and social media. We can add painted or sewn lettering directly to the light bars for a storefront
                that doubles as a sign.
              </p>

              <SwatchFigure
                side="left"
                src={captainNavyAsset.url}
                alt="Sunbrella® Beaufort Captain Navy 4708 awning fabric with navy blue and natural white stripes"
                caption="Beaufort Captain Navy 4708"
              />

              <p>
                <strong className="text-navy-deep">Beaufort Captain Navy</strong> is our most-requested traditional stripe:
                deep navy alternating with natural white for that timeless nautical, ranch-house or country-club look. It
                sits right at home under limestone, brick and white trim, and it's the stripe we reach for on boat covers,
                stock trailer tops, cabanas and residential entry awnings when a customer says "classic."
              </p>

              <p className="clear-both pt-2 text-sm text-navy-deep/60">
                These are four of hundreds. Note the fabric number and color name from any Sunbrella® stripe or solid and
                we'll quote it — same shop, same stitching, same warranty.
              </p>
            </div>

          </div>
        </section>


        {/* BROWSE CTA */}
        <section className="bg-white py-14 lg:py-20">
          <div className="mx-auto max-w-3xl px-4 text-center lg:px-8">
            <div className="mb-2 font-display text-xs uppercase tracking-[0.3em] text-red">★ Browse Swatches</div>
            <h2 className="font-display text-3xl font-bold uppercase text-navy-deep md:text-4xl">
              Pick Your Colors on Sunbrella.com
            </h2>
            <p className="mt-4 text-navy-deep/80">
              See every current Sunbrella® color, pattern and collection straight from the manufacturer. Note your favorites, then send them to us and we'll build it.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a
                href="https://www.sunbrella.com/browse-fabrics/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-navy-deep px-7 py-3.5 font-display text-sm uppercase tracking-wider text-white transition hover:bg-red"
              >
                <ExternalLink className="h-4 w-4" /> Open Sunbrella® Fabric Browser
              </a>
              <a
                href="#send-us-your-picks"
                className="inline-flex items-center gap-2 border-2 border-navy-deep px-7 py-3.5 font-display text-sm uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
              >
                Send Us Your Picks
              </a>
            </div>
            <p className="mt-3 text-xs text-navy-deep/60">
              Sunbrella® is a registered trademark of Glen Raven, Inc. Texas Made Shade is an authorized fabricator.
            </p>

            <div id="send-us-your-picks" className="scroll-mt-24 pt-16 text-left">
              <div className="text-center">
                <div className="mb-2 font-display text-xs uppercase tracking-[0.3em] text-red">★ Send Us Your Picks</div>
                <h3 className="font-display text-2xl font-bold uppercase text-navy-deep md:text-3xl">
                  Tell Us What You Found
                </h3>
                <p className="mx-auto mt-3 max-w-xl text-sm text-navy-deep/70">
                  Pick your project, drop the 6-digit Sunbrella® fabric number and color name. We'll email you back with a quote.
                </p>
              </div>
              <FabricPickForm />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-navy-deep py-14 text-white">
          <div className="mx-auto max-w-7xl px-4 text-center lg:px-8">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Don't see what you need?</div>
            <h2 className="mt-2 font-display text-3xl font-bold uppercase md:text-4xl">
              We can get and ship Sunbrella® colors from our shop.
            </h2>
            <p className="mt-4 text-white/80">
              Stop by our Seguin location to flip through full sample books, or{" "}
              <Link to="/contact" className="border-b border-white/40 hover:text-red">contact us</Link>{" "}
              and we'll bring colors to you.
            </p>
            <Link
              to="/shop"
              className="mt-6 inline-flex items-center gap-2 bg-red px-7 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
            >
              <ShoppingBag className="h-4 w-4" /> Or browse Ready-to-Order
            </Link>
          </div>
        </section>
      </main>

      <LetsTalkForm
        imageSrc={fabricRollsAsset.url}
        imageAlt="Rolls of Sunbrella® solution-dyed acrylic fabric in neutral, tan and blue tones"
      />
      <SiteFooter />
    </div>
  );
}

function SwatchFigure({
  side,
  src,
  alt,
  caption,
}: {
  side: "left" | "right";
  src: string;
  alt: string;
  caption: string;
}) {
  const floatSide =
    side === "right"
      ? "sm:float-right sm:clear-right sm:ml-5 md:ml-7"
      : "sm:float-left sm:clear-left sm:mr-5 md:mr-7";

  return (
    <figure
      className={`mb-5 mt-6 w-full first:mt-0 sm:mb-4 sm:mt-1 sm:w-40 md:w-48 lg:w-60 ${floatSide}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        width={800}
        height={800}
        className="h-24 w-full border-2 border-navy-deep/10 object-cover shadow-card sm:h-auto sm:aspect-square"
      />
      <figcaption className="mt-2 font-display text-[10px] uppercase leading-snug tracking-[0.2em] text-navy-deep/60">
        {caption}
      </figcaption>
    </figure>
  );
}
